import { RecordableEvent, RecordableEventCategory, RecordableEventName } from "../../lib/Events";
import { SerializableError } from "../../lib/utils/SerializedException";
import { ProcessInfo } from "../ProcessInfo";
import { type DbRecordedEvent, type UUID, NameOfIndexForRecordingUuidThenNumericIndex, getRangeForAllRecordsWithRecordingUuid, NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, DbRecordedEventByCategoryAndName, NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName, RecordedEvent, NameOfIndexForRecordingUuidThenCategoryThenName, NameOfIndexForRecordingUuidThenCategoryThenEventIndex, DbRecordedEventByCategory, NameOfIndexForReceivedEventUuidThenEventName, DbRecordedEventByName, NameOfIndexForEventListenerUuidThenEventName } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";


export class DbEventsTableReader {
	get db() { return requireEvidenceDatabase() };

	getEvents = async () => {
		return await (await this.db).getAllFromIndex("events", NameOfIndexForRecordingUuidThenNumericIndex, getRangeForAllRecordsWithRecordingUuid(this.recordingUuid));
	} 

	numberOfEvents = async () => (await this.db).count("events", IDBKeyRange.only([this.recordingUuid]));

	forEventsAsync = async (callback: (event: DbRecordedEvent) => Promise<void>) => {
		const db = await this.db;
		const eventIndexes = await db.getAllKeysFromIndex("events", NameOfIndexForRecordingUuidThenNumericIndex, getRangeForAllRecordsWithRecordingUuid(this.recordingUuid));
		for (const index of eventIndexes) {
			const event = await db.getFromIndex("events", NameOfIndexForRecordingUuidThenNumericIndex, [this.recordingUuid, index]);
			if (event != null) {
				await callback(event);
			}
		}
	};

	/**
	 * 
	 * @param callback Must be synchronous and return "undefined" to ensure promises aren't accidentally called
	 */
	forEvents = async (callback: (event: DbRecordedEvent) => undefined) => {
		const tx = (await this.db).transaction("events", "readonly");
		const bound = IDBKeyRange.bound([this.recordingUuid, 0], [this.recordingUuid, Number.MAX_SAFE_INTEGER], false, false);
		for await (const cursor of tx.store.index(NameOfIndexForRecordingUuidThenNumericIndex).iterate(bound)) {
			callback(cursor.value);
		}
		tx.commit();
		await tx.done;
	};

	/**
	 * 
	 * @param callback Must be synchronous and return "undefined" to ensure promises aren't accidentally called
	 */
	forEventsStartAtIndex = async (startIndex: number, callback: (event: DbRecordedEvent) => undefined) => {
		const tx = (await this.db).transaction("events", "readonly");
		const bound = IDBKeyRange.bound([this.recordingUuid, startIndex], [this.recordingUuid, Number.MAX_SAFE_INTEGER], false, false);
		for await (const cursor of tx.store.index(NameOfIndexForRecordingUuidThenNumericIndex).iterate(bound)) {
			callback(cursor.value);
		}
		tx.commit();
		await tx.done;
	};


	/**
	 * 
	 * @param callback Must be synchronous and return "undefined" to ensure promises aren't accidentally called
	 */
	forEventsWithCategory =  <
		EVENT_CATEGORY extends RecordableEventCategory
	>(
		category: EVENT_CATEGORY,
		lowerBoundInclusive: number = 0
	) => async (
		callback: (event: DbRecordedEventByCategory<EVENT_CATEGORY>) => undefined
	): Promise<void> => {
		const db = await this.db;
		const tx = (await this.db).transaction("events", "readonly");
		const index = tx.store.index(NameOfIndexForRecordingUuidThenCategoryThenEventIndex);
		const bound = IDBKeyRange.bound([this.recordingUuid, category, lowerBoundInclusive], [this.recordingUuid, category, Number.MAX_SAFE_INTEGER], false, false);
		for await (const cursor of index.iterate(bound)) {
			callback(cursor.value as DbRecordedEventByCategory<EVENT_CATEGORY>);
		}
		tx.commit();
		await tx.done;
	};

	/**
	 * 
	 * @param callback Must be synchronous and return "undefined" to ensure promises aren't accidentally called
	 */
	forEventsWithCategoryAndName =  <
		EVENT_CATEGORY extends RecordableEventCategory,
		EVENT_NAME extends RecordableEventName
	>(
		category: EVENT_CATEGORY,
		name: EVENT_NAME,
		lowerBoundInclusive: number = 0
	)  => async (
		callback: (event: DbRecordedEventByCategoryAndName<EVENT_CATEGORY, EVENT_NAME>) => undefined
	): Promise<void> => {
		const tx = (await this.db).transaction("events", "readonly");
		const index = tx.store.index(NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex);
		const bound = IDBKeyRange.bound([this.recordingUuid, category, name, lowerBoundInclusive], [this.recordingUuid, category, name, Number.MAX_SAFE_INTEGER], false, false);
		for await (const cursor of index.iterate(bound)) {
			callback(cursor.value as DbRecordedEventByCategoryAndName<EVENT_CATEGORY, EVENT_NAME>);
		}
		tx.commit();
		await tx.done;
	};

	countEventsOfType = async <
		EVENT_CATEGORY extends RecordableEventCategory,
		EVENT_NAME extends RecordableEventName
	>(category: EVENT_CATEGORY, name: EVENT_NAME): Promise<number> => {
		const bound = IDBKeyRange.bound([this.recordingUuid, category, name, 0], [this.recordingUuid, category, name, Number.MAX_SAFE_INTEGER], false, false);
		return (await this.db).countFromIndex("events", NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, bound);
	};

	getEvent = async (index: number) => await (await this.db).getFromIndex("events", NameOfIndexForRecordingUuidThenNumericIndex, [this.recordingUuid, index]);

	getEventsByCategoryThenNameThenRequest = <
		CATEGORY extends RecordableEventCategory,
		NAME extends RecordableEventName
	>(
		category: CATEGORY,
		name: NAME
	) => async (requestId: string) => {
		try {
			return	await ((await this.db).getAllFromIndex(
				"events",
				NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName,  [this.recordingUuid, requestId, category, name]
			) as Promise<DbRecordedEventByCategoryAndName<CATEGORY, NAME>[]>);
		} catch (e) {
			ProcessInfo.errorLog("Failed to get event by category, name, and request ID", [this.recordingUuid, requestId, category, name], SerializableError.from(e, 'getEventByCategoryThenNameThenRequest'));
			throw e;
		}
	}

	getEventsForCategoryAndName = async <CATEGORY extends DbRecordedEvent["category"], NAME extends DbRecordedEvent["eventName"]>(
		{category, name,
			startingAtIndexExclusive = Number.MIN_SAFE_INTEGER,
			startingAtIndexInclusive = startingAtIndexExclusive + (startingAtIndexExclusive < Number.MIN_SAFE_INTEGER ? 1 : 0),
		}:
		{category: CATEGORY, name: NAME, startingAtIndexInclusive?: number, startingAtIndexExclusive?: number}
	) => {
		const bound = IDBKeyRange.bound(
			[this.recordingUuid, category, name, startingAtIndexInclusive],
			[this.recordingUuid, category, name, Number.MAX_SAFE_INTEGER],
			false, false);
		const db = await this.db;
		return await db.getAllFromIndex(
			"events", NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex, bound
		) as DbRecordedEventByCategoryAndName<CATEGORY, NAME>[];
	}

	getEventsForCategory = async <CATEGORY extends DbRecordedEvent["category"]>(
		{category, startingAtEventIndex=0}:
		{category: CATEGORY, startingAtEventIndex?: number}
	) => {
		const bound = IDBKeyRange.bound(
			[this.recordingUuid, category, startingAtEventIndex],
			[this.recordingUuid, category, Number.MAX_SAFE_INTEGER],
			false, false);
		const db = await this.db;
		return await db.getAllFromIndex(
			"events", NameOfIndexForRecordingUuidThenCategoryThenEventIndex, bound
		) as DbRecordedEventByCategory<CATEGORY>[];
	}

	getEventWithReceivedEventUuid = async <EVENT_NAME extends Extract<RecordableEvent, {raw: {receivedEventUuid: UUID}}>["eventName"]>(
		receivedEventUuid: UUID, eventName: EVENT_NAME
	) => {
		const bound = IDBKeyRange.only([receivedEventUuid, eventName]);
		const db = await this.db;
		return await db.getFromIndex(
			"events", NameOfIndexForReceivedEventUuidThenEventName, bound
		) as DbRecordedEventByName<typeof eventName> | undefined;
	}

	getEventWithEventListenerUuid = async <EVENT_NAME extends Extract<RecordableEvent, {raw: {eventListenerUuid: UUID}}>["eventName"]>(
		eventListenerUuid: UUID,
		eventName: EVENT_NAME
	) => {
		const bound = IDBKeyRange.only([eventListenerUuid, eventName]);
		const db = await this.db;
		return await db.getFromIndex(
			"events", NameOfIndexForEventListenerUuidThenEventName, bound
		) as DbRecordedEventByName<typeof eventName> | undefined
	}


	/**
	 * Returns the recording record associated with the class instance from the database.
	 */
	// recording = async () => await (await this.db).get("recordings", this.recordingUuid);


	protected constructor(public readonly recordingUuid: UUID) {}

	static #forRecordingUuid = new Map<UUID, DbEventsTableReader>();
	static forRecordingUuid = (recordingUuid: UUID) => DbEventsTableReader.#forRecordingUuid.get(recordingUuid) ?? (() => {
		const evidenceDbForRecording = new DbEventsTableReader(recordingUuid);
		DbEventsTableReader.#forRecordingUuid.set(recordingUuid, evidenceDbForRecording);
		return evidenceDbForRecording;
	})();

	static removeRecordingUuid = (recordingUuid: UUID) => DbEventsTableReader.#forRecordingUuid.delete(recordingUuid);


}


